import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard } from '@fortawesome/pro-regular-svg-icons';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import { faPaypal } from '@fortawesome/free-brands-svg-icons';
import Box, { BoxLabel } from './Box';
import useAutoRenewalUrls from './urls';
import { AutoRenewalDetail, Props } from './types';

type PaymentMethodProps = Pick<Props, 'confirmUrlTemplate' | 'updatePaymentUrlTemplate'> & {
  autoRenewal: AutoRenewalDetail;
  /** The renewal failed on this card, so the way out is the confirm page, not the editor. */
  hasIssueWithPayment?: boolean;
};

/**
 * What the subscription renews against.
 *
 * PayPal has nothing to edit here — the authorization lives at PayPal — so it gets no
 * link unless the payment is what failed, in which case the confirm page can take a new
 * one. That asymmetry is the source's and is the reason the link is conditional.
 */
export default function PaymentMethod({
  autoRenewal,
  hasIssueWithPayment,
  confirmUrlTemplate,
  updatePaymentUrlTemplate,
}: PaymentMethodProps) {
  const { confirmUrl, updatePaymentUrl } = useAutoRenewalUrls();
  const paymentProfile = autoRenewal.payment_profile;
  const upcoming = autoRenewal.upcoming_transaction;

  if (!paymentProfile) {
    return null;
  }

  const isCreditCard = !!paymentProfile.credit_card;
  const updatePaymentMethodUrl =
    hasIssueWithPayment && upcoming
      ? confirmUrl(confirmUrlTemplate, upcoming)
      : updatePaymentUrl(updatePaymentUrlTemplate, paymentProfile.id);

  return (
    <>
      <BoxLabel>Payment method</BoxLabel>
      <Box>
        <div className="font-semibold">
          <FontAwesomeIcon icon={isCreditCard ? faCreditCard : faPaypal} className="mr-1.5" />
          {isCreditCard
            ? `${paymentProfile.credit_card?.type || 'Card'} ending in ${paymentProfile.credit_card?.last4}`
            : 'PayPal'}
        </div>

        {!isCreditCard && paymentProfile.payment_email && (
          <div className="text-sm text-slate-500 font-medium mt-2">{paymentProfile.payment_email}</div>
        )}

        {(hasIssueWithPayment || isCreditCard) && (
          <a
            href={updatePaymentMethodUrl}
            className="inline-block font-semibold text-sm text-sky-600 hover:text-sky-700 active:text-sky-800 hover:underline mt-2"
          >
            Update payment method
            <FontAwesomeIcon icon={faAngleRight} className="ml-1" />
          </a>
        )}
      </Box>
    </>
  );
}
